/**
 * PowersureLandingHero — premium dark hero voor landing.
 *
 * Drop-in vervanger voor `HeroSection` met dezelfde i18n-keys + tracking,
 * maar in de Powersure premium dark/emerald lijn. Toont:
 *   - badge + titel + ondertitel
 *   - micro-bullets ("Gratis · 1 minuut · Geen verplichting")
 *   - mini app-preview (dashboard-card mockup, dark-stijl)
 *   - prijsblok € 9,95 + primaire CTA + secundaire CTA
 *
 * De Hero is volledig opt-in: laat de rest van HomePage onaangeroerd.
 */
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Plug,
  CheckCircle2,
  Sun,
  Clock,
  ArrowDownToLine,
  Zap,
} from 'lucide-react';
import PowersureCard from '@/components/powersure-ui/PowersureCard';
import FeatureBullet from '@/components/powersure-ui/FeatureBullet';
import PriceBlock from '@/components/powersure-ui/PriceBlock';
import { psCls } from '@/lib/powersure-theme';
import LandingPrimaryCta, { LandingSecondaryCta } from '@/components/home/LandingPrimaryCta';

const TOP_KANSEN = [
  {
    icon: Sparkles,
    title: 'Eigen stroom slimmer gebruiken',
    body: 'Minder terugleveren, meer zelf benutten.',
  },
  {
    icon: Plug,
    title: 'Apparaten koppelen',
    body: 'P1, omvormer, laadpaal, warmtepomp of boiler.',
  },
  {
    icon: CheckCircle2,
    title: 'Beste volgende stap zien',
    body: 'Elke maand een concreet advies, geen ruis.',
  },
];

export default function PowersureLandingHero({ onLanguageClick }) {
  const { t } = useTranslation();

  return (
    <section
      className="relative py-10 sm:py-14 lg:py-20"
      style={{ background: 'linear-gradient(180deg, #0a1410 0%, #0e1b16 100%)' }}
      data-testid="hero-section"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* LEFT — text + CTA */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className={psCls.iconBadge}>
                <Zap className="w-4 h-4" />
              </span>
              <span className={psCls.label}>Powersure Energy Control</span>
            </div>

            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white leading-tight mb-4 sm:mb-5"
              style={{ fontFamily: 'Outfit' }}
              data-testid="hero-h1"
            >
              Meer grip op uw zonnestroom
            </h1>

            <p
              className="text-base sm:text-lg text-white/75 leading-relaxed mb-5 sm:mb-6 max-w-xl"
              data-testid="hero-subtitle"
            >
              Powersure laat zien welke apparaten u slim kunt koppelen, waar u stroom laat liggen
              en welke stap nu het meeste oplevert — zonder ingewikkelde dashboards.
            </p>

            {/* Friction chips */}
            <div
              className="flex flex-wrap gap-1.5 mb-5"
              data-testid="hero-friction-chips"
            >
              <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 font-medium">
                Direct starten
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs px-2.5 py-1 rounded-full bg-sky-500/15 text-sky-300 border border-sky-400/30 font-medium">
                Maandelijks opzegbaar
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs px-2.5 py-1 rounded-full bg-white/8 text-white/80 border border-white/15 font-medium">
                Geen leverancierswissel
              </span>
            </div>

            {/* Top 3 kansen — kerntje van wat Powersure doet */}
            <ul className="space-y-2.5 mb-6 max-w-md" data-testid="hero-top3">
              {TOP_KANSEN.map(({ icon: Icon, title, body }, i) => (
                <li key={i}>
                  <FeatureBullet icon={Icon} title={title} body={body} testId={`hero-kans-${i + 1}`} />
                </li>
              ))}
            </ul>

            {/* Prijsblok — direct duidelijk */}
            <PriceBlock testId="hero-price" className="max-w-md mb-5" />

            {/* CTA's — direct naar checkout, geen vragen vooraf */}
            <div className="flex flex-col sm:flex-row gap-2.5 max-w-md">
              <LandingPrimaryCta
                ctaId="hero_start_subscription"
                label="Start Mijn Powersure"
                testId="hero-start-scan-btn"
              />
              <LandingSecondaryCta
                targetTestId="powersure-app-preview-section"
                label="Bekijk hoe het werkt"
                testId="hero-how-it-works-btn"
              />
            </div>

            <p className="text-[11px] text-white/45 mt-3 leading-relaxed max-w-md">
              Veilig betalen · geen leverancierswissel · geen automatische
              sturing zonder uw goedkeuring.
            </p>
          </div>

          {/* RIGHT — dark app-preview mockup (desktop only) */}
          <div className="relative hidden lg:block">
            <PowersureCard padding="p-0" className="max-w-md">
              {/* Mockup header */}
              <div className="border-b border-white/10 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.7)]" aria-hidden />
                  <span className="text-xs font-medium text-white/85">
                    Powersure — uw woning
                  </span>
                </div>
                <span className="text-[10px] text-white/40 uppercase tracking-wider">indicatief</span>
              </div>

              <div className="p-5 space-y-4">
                {/* Eigen Zonnestroom Score */}
                <div className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid="hero-mockup-zonnestroom-score">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4 text-amber-300" />
                      <span className="text-xs font-semibold text-white">
                        Eigen Zonnestroom Score
                      </span>
                    </div>
                    <span className="text-[10px] text-white/50">vorige maand 65%</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-4xl font-light text-emerald-300"
                      style={{ fontFamily: 'Outfit' }}
                    >
                      72%
                    </span>
                    <span className="text-xs text-white/60">zelf gebruikt</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full bg-white/8 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400 rounded-full" style={{ width: '72%' }} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/25 p-3" data-testid="hero-mockup-bespaarkans">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                      <span className="text-[11px] font-semibold text-emerald-200">Bespaarkans</span>
                    </div>
                    <p className="text-sm font-medium text-white">Aanbevolen</p>
                    <p className="text-[10px] text-white/55 mt-0.5">na koppeling concrete inzicht</p>
                  </div>
                  <div className="rounded-lg bg-amber-500/10 border border-amber-400/25 p-3" data-testid="hero-mockup-teruglever">
                    <div className="flex items-center gap-1.5 mb-1">
                      <ArrowDownToLine className="w-3.5 h-3.5 text-amber-300" />
                      <span className="text-[11px] font-semibold text-amber-200">Teruglever</span>
                    </div>
                    <p className="text-sm font-medium text-white">11:00–13:00</p>
                    <p className="text-[10px] text-white/55 mt-0.5">piek · 1,9 kWh terug</p>
                  </div>
                </div>

                <div className="rounded-lg bg-white/5 border border-white/10 p-3" data-testid="hero-mockup-next-step">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Clock className="w-3.5 h-3.5 text-emerald-300" />
                    <span className="text-[11px] font-semibold text-white">Slimste volgende stap</span>
                  </div>
                  <p className="text-sm text-white/85">
                    Verplaats vaatwasser naar <span className="font-medium text-emerald-200">13:30</span> — meer eigen stroom benut.
                  </p>
                </div>

                <p className="text-[10px] text-white/45 leading-relaxed pt-1 border-t border-white/8" data-testid="hero-mockup-safety">
                  Automatische sturing: alleen waar veilig, ondersteund en door u goedgekeurd.
                </p>
              </div>
            </PowersureCard>
          </div>
        </div>
      </div>
    </section>
  );
}
